import App from 'components/templates/app';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router';
import Section from 'components/Section';
import Subtitle from 'components/Subtitle';
import {
  Box,
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  List,
  MenuItem,
  OutlinedInput,
  Pagination,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
} from '@mui/material';
import Loader from 'components/SectionLoader';
import Button from 'components/Button';
import { showErrorToast } from 'features/toastSlice';
import TagsSelectDialog from 'components/dialogs/TagFilterDialog';
import Autocomplete from 'components/Autocomplete';
import VisibilitySharpIcon from '@mui/icons-material/VisibilitySharp';
import QueryStatsSharpIcon from '@mui/icons-material/QueryStatsSharp';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { fetchAllOrganizations, getDoctorNames } from 'api/portal';
import { getAcquisitionsByOrg, getDoctorsByOrg, getTagsByOrg, reportAuthorizationByOrg } from 'api/analytics';
import { getReport } from 'api/client-phi';
import { ANALYTICS_REPORTS_URL } from 'config/settings';
import Cookies from 'js-cookie';
import Fade from 'components/Fade';
import AnonymizedAcquisitionRow from 'components/tables/AnonymizedAcquisitionRow';
import { deserializeTabularData } from 'utils/serialization';
import {
  clearReportDeepLinkSearchParams,
  normalizeReportDeepLinkAction,
} from 'utils/postLoginRedirect';
import AcquisitionMenuItem from 'components/AcquisitionMenuItem';
import styles from './styles.module.css';

const LIMIT_OPTIONS = [10, 20, 50, 100];
const ACQUISITIONS_FETCH_MAX_ATTEMPTS = 10;
const RETRY_DELAY_MS = 800;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Rede, timeout/abort do Axios no cliente, e 5xx (incl. 504 gateway timeout). */
function isRetriableAcquisitionsAxiosError(error) {
  if (error?.code === 'ECONNABORTED') {
    return true;
  }
  if (!error?.response) {
    return true;
  }
  const { status } = error.response;
  return status === 500 || status === 502 || status === 503 || status === 504;
}

function parseAcquisitionsResponseBody(response) {
  let responseData;
  if (typeof response.data === 'string') {
    const jsonString = response.data
      .replace(/NaN/g, 'null')
      .replace(/,,/g, ',null,')
      .replace(/,\]/g, ',null]')
      .replace(/,\}/g, ',null}');
    try {
      responseData = JSON.parse(jsonString);
    } catch (parseError) {
      return { kind: 'parse_error' };
    }
  } else {
    responseData = response.data;
  }
  const { data, error } = responseData;
  if (!error) {
    return { kind: 'success', data };
  }
  return { kind: 'business_error' };
}

function AdminOrganizationReports() {
  const { t } = useTranslation();

  const ALL_DOCTORS = {
    id: -1,
    label: t('physio.physio-personal.dropdown-filter-doctor-all'),
  };

  const LOADING_DOCTOR = {
    id: -1,
    label: t('physio.physio-personal.select-loading'),
  };

  const LOADING_ORGANIZATION = {
    id: 1,
    physio_org_id: 100,
    label: t('physio.physio-personal.select-loading'),
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const [organizations, setOrganizations] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [tags, setTags] = useState([]);

  const [protocol, setProtocol] = useState('');
  const [organization, setOrganization] = useState(LOADING_ORGANIZATION);
  const [doctor, setDoctor] = useState(LOADING_DOCTOR);
  const [selectedTags, setSelectedTags] = useState([]);

  const [acquisitions, setAcquisitions] = useState([]);
  const [acquisitionsFetchSucceeded, setAcquisitionsFetchSucceeded] = useState(null);

  const [selectedAcquisition, setSelectedAcquisition] = useState({});
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [limitPerPage, setLimitPerPage] = useState(10);

  const [showFilterByTags, setShowFilterByTags] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  const acquisitionsFetchIdRef = useRef(0);
  const suppressAutoOrgFetchRef = useRef(false);
  /**
   * When deep link calls setOrganization, physio effect cleanup must not bump fetch id
   * (would orphan the deep-link fetch and cause endless loading).
   */
  const skipNextPhysioEffectCleanupBumpRef = useRef(false);
  const processedAdminReportDeepLinkRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();

  const HOME = t('physio.breadcrumbs.home');
  const TITLE = t('physio.breadcrumbs.admin-title-organization', {
    organization: organization?.name || t('physio.breadcrumbs.admin-title-no-org-selected'),
  });

  const breadcrumbs = [
    {
      path: '/',
      title: HOME,
    },
    {
      path: '/organization/physio',
      title: TITLE,
      isActive: true,
    },
  ];

  const onFetchDataFailure = () => dispatch(showErrorToast('messages.fetch-data-failure'));

  const fetchDoctorNames = (values) => {
    getDoctorNames(values)
      .then((response) => {
        const { data } = response;
        setLoadingDoctors(false);

        setDoctor(ALL_DOCTORS);
        setDoctors([ALL_DOCTORS, ...data.map((doctor) => ({ ...doctor, label: doctor.full_name }))]);
      })
      .catch(() => {
        setLoadingDoctors(false);
        onFetchDataFailure();
      });
  };

  const fetchDoctorByOrg = (orgId) => {
    setLoadingDoctors(true);
    setDoctor(LOADING_DOCTOR);
    getDoctorsByOrg(orgId)
      .then((response) => {
        const { data } = response.data;

        if (data && data.values.length > 0) {
          fetchDoctorNames(data.values);
        } else {
          setDoctors([]);
          setDoctor(null);
          setLoadingDoctors(false);
        }
      })
      .catch(() => {
        setDoctors([]);
        setDoctor(null);
        setLoadingDoctors(false);
        onFetchDataFailure();
      });
  };

  const fetchAcquisitionsByOrg = ({
    physioOrgId,
    selectedTags,
    protocol,
    doctor,
    currentPage,
    limitPerPage,
    onAcquisitionsSuccess,
  }) => {
    const doctorId = doctor && doctor.id !== -1 ? doctor.id : '';

    setLoading(true);
    setCurrentPage(currentPage);
    setAcquisitionsFetchSucceeded(null);

    const payload = {
      filters: {
        tags: selectedTags,
        acquisition_id: [protocol],
        doctor_id: String(doctorId),
      },
      paging: {
        page: currentPage,
        limit: limitPerPage,
      },
    };

    acquisitionsFetchIdRef.current += 1;
    const fetchId = acquisitionsFetchIdRef.current;

    const fail = () => {
      if (fetchId !== acquisitionsFetchIdRef.current) {
        return;
      }
      setAcquisitions([]);
      setLoading(false);
      setAcquisitionsFetchSucceeded(false);
      onFetchDataFailure();
    };

    const applySuccessData = (data) => {
      if (data?.acquisitions?.values?.length > 0) {
        const acquisitionKeys = data.acquisitions.keys;
        setAcquisitions(
          data.acquisitions.values.map((acquisition) => deserializeTabularData(acquisitionKeys, acquisition)),
        );
        setCount(Math.ceil(data.acquisitions.paging.count / limitPerPage));
      } else {
        setAcquisitions([]);
        setCount(0);
      }
    };

    const runAttempt = async (attempt) => {
      if (fetchId !== acquisitionsFetchIdRef.current) {
        return;
      }

      try {
        const response = await getAcquisitionsByOrg(payload, physioOrgId);

        if (fetchId !== acquisitionsFetchIdRef.current) {
          return;
        }

        const parsed = parseAcquisitionsResponseBody(response);

        if (parsed.kind === 'success') {
          if (fetchId !== acquisitionsFetchIdRef.current) {
            return;
          }

          const { data } = parsed;

          setLoading(false);
          setAcquisitionsFetchSucceeded(true);
          applySuccessData(data);
          onAcquisitionsSuccess?.(data);
          return;
        }

        if (parsed.kind === 'parse_error' || parsed.kind === 'business_error') {
          fail();
        }
      } catch (error) {
        if (fetchId !== acquisitionsFetchIdRef.current) {
          return;
        }

        if (isRetriableAcquisitionsAxiosError(error) && attempt < ACQUISITIONS_FETCH_MAX_ATTEMPTS - 1) {
          await sleep(RETRY_DELAY_MS);
          if (fetchId !== acquisitionsFetchIdRef.current) {
            return;
          }
          await runAttempt(attempt + 1);
          return;
        }

        fail();
      }
    };

    (async () => {
      await runAttempt(0);
    })();
  };

  const onItensPerPageChange = (event) => {
    const newLimit = event.target.value;
    setLimitPerPage(newLimit);

    fetchAcquisitionsByOrg({
      physioOrgId: organization.physio_org_id,
      selectedTags,
      protocol,
      doctor,
      currentPage: 1,
      limitPerPage: newLimit,
    });
  };

  const fetchTagsByOrg = (orgId) => {
    getTagsByOrg(orgId)
      .then((response) => {
        const { data, error } = response.data;

        if (error) {
          onFetchDataFailure();
          return;
        }

        setTags(data.values);
      })
      .catch(() => {
        onFetchDataFailure();
      });
  };

  useEffect(() => {
    if (organization?.physio_org_id) {
      const hasReportDeepLink =
        Boolean(searchParams.get('umi')?.trim()) && Boolean(searchParams.get('org_id'));
      if (hasReportDeepLink) {
        return undefined;
      }

      if (suppressAutoOrgFetchRef.current) {
        suppressAutoOrgFetchRef.current = false;
      } else {
        if (acquisitions.length === 0) {
          setDoctors([]);
          setDoctor(null);
          setLoadingDoctors(false);
        } else {
          setDoctors([ALL_DOCTORS]);
          setDoctor(ALL_DOCTORS);
          setLoadingDoctors(false);
        }

        fetchAcquisitionsByOrg({
          physioOrgId: organization.physio_org_id,
          selectedTags: [],
          protocol: '',
          doctor: ALL_DOCTORS,
          currentPage: 1,
          limitPerPage,
          onAcquisitionsSuccess: (data) => {
            fetchTagsByOrg(organization.physio_org_id);

            if (!data?.acquisitions?.values?.length) {
              setDoctors([]);
              setDoctor(null);
              setLoadingDoctors(false);
            } else {
              fetchDoctorByOrg(organization.physio_org_id);
            }
          },
        });
      }
    }
    return () => {
      if (skipNextPhysioEffectCleanupBumpRef.current) {
        skipNextPhysioEffectCleanupBumpRef.current = false;
        return;
      }
      acquisitionsFetchIdRef.current += 1;
    };
  }, [organization?.physio_org_id]);

  useEffect(() => {
    const searchParams = new URLSearchParams();
    searchParams.set('page_size', 9999);

    fetchAllOrganizations(searchParams)
      .then((response) => {
        const { results } = response.data;

        const sortedOrganizations = results.map((org) => ({
          ...org,
          id: org.id,
          physio_org_id: +org.physio_org_id,
          label: org.name
        }));

        setOrganizations(sortedOrganizations);
        setOrganization(sortedOrganizations.find(org => org.physio_org_id === LOADING_ORGANIZATION.physio_org_id));
      })
      .catch(() => onFetchDataFailure());
  }, []);

  const filterAcquisitions = () => {
    fetchAcquisitionsByOrg({
      physioOrgId: organization.physio_org_id,
      selectedTags,
      protocol,
      doctor,
      currentPage: 1,
      limitPerPage,
    });
  };

  const clearFilters = () => {
    setProtocol('');
    setDoctor(ALL_DOCTORS);
    setSelectedTags([]);

    fetchAcquisitionsByOrg({
      physioOrgId: organization.physio_org_id,
      selectedTags: [],
      protocol: '',
      doctor: ALL_DOCTORS,
      currentPage: 1,
      limitPerPage,
    });
  };

  const onPaginationChange = (event, value) => {
    fetchAcquisitionsByOrg({
      physioOrgId: organization.physio_org_id,
      selectedTags,
      protocol,
      doctor,
      currentPage: value,
      limitPerPage,
    });
  };

  const setAcquisitionAsDownloading = (targetProtocolId, isDownloading) => {
    setAcquisitions((prev) =>
      prev.map((acquisition) => {
        const protocolId = acquisition.acquisition_id;
        return {
          ...acquisition,
          isDownloading: protocolId === targetProtocolId ? isDownloading : acquisition.isDownloading,
        };
      }),
    );
  };

  const openReportWhenAuthorized = (protocolId, url) => {
    let timeOutCount = 0;

    const interval = setInterval(() => {
      fetch(url).then((res) => {
        timeOutCount += 1;
        // open the report in a new tab
        if (res.status === 200) {
          setAcquisitionAsDownloading(protocolId, false);
          window.open(url, '_blank');
          clearInterval(interval);
        }
        // timeout after 3 minutes (180 attempts)
        if (timeOutCount >= 180) {
          // eslint-disable-next-line no-alert
          alert('Report error, please try again');
          setAcquisitionAsDownloading(protocolId, false);
          clearInterval(interval);
        }
      });
    }, 1000);
  };

  const downloadReport = (acquisition, orgForReport = organization) => {
    const protocolId = acquisition.acquisition_id;
    const orgId = orgForReport.physio_org_id;

    setAcquisitionAsDownloading(protocolId, true);

    reportAuthorizationByOrg(
      {
        acquisition_id: protocolId,
        report_language: i18next.language,
        report_format: 'PDF',
      },
      orgId,
    )
      .then((response) => {
        const { data, error } = response.data;

        if (error) {
          setAcquisitionAsDownloading(protocolId, false);
          onFetchDataFailure();
          return;
        }

        getReport(orgId, data)
          .then((response) => {
            const { data } = response;
            openReportWhenAuthorized(protocolId, data.data);
          })
          .catch(() => {
            setAcquisitionAsDownloading(protocolId, false);
            onFetchDataFailure();
          });
      })
      .catch(() => {
        setAcquisitionAsDownloading(protocolId, false);
        onFetchDataFailure();
      });
  };

  useEffect(() => {
    if (organizations.length === 0) {
      return undefined;
    }

    const umi = searchParams.get('umi');
    const orgIdParam = searchParams.get('org_id');
    if (!umi?.trim() || !orgIdParam) {
      return undefined;
    }

    const linkKey = `${umi}:${orgIdParam}:${searchParams.get('action') || ''}`;
    if (processedAdminReportDeepLinkRef.current === linkKey) {
      return undefined;
    }

    const match = organizations.find((o) => String(o.id) === String(orgIdParam));
    if (!match) {
      processedAdminReportDeepLinkRef.current = linkKey;
      clearReportDeepLinkSearchParams(setSearchParams);
      onFetchDataFailure();
      return undefined;
    }

    const action = normalizeReportDeepLinkAction(searchParams.get('action'));
    const umiTrim = umi.trim();

    processedAdminReportDeepLinkRef.current = linkKey;

    skipNextPhysioEffectCleanupBumpRef.current = true;
    suppressAutoOrgFetchRef.current = true;
    setOrganization(match);
    setProtocol(umiTrim);

    fetchAcquisitionsByOrg({
      physioOrgId: match.physio_org_id,
      selectedTags: [],
      protocol: umiTrim,
      doctor: ALL_DOCTORS,
      currentPage: 1,
      limitPerPage,
      onAcquisitionsSuccess: (data) => {
        fetchTagsByOrg(match.physio_org_id);

        if (!data?.acquisitions?.values?.length) {
          setDoctors([]);
          setDoctor(null);
          setLoadingDoctors(false);
        } else {
          fetchDoctorByOrg(match.physio_org_id);
        }

        if (action === 'open' && data?.acquisitions?.keys && data?.acquisitions?.values?.length) {
          const acquisitionKeys = data.acquisitions.keys;
          const rows = data.acquisitions.values.map((row) => deserializeTabularData(acquisitionKeys, row));
          const acq = rows.find((a) => String(a.acquisition_id) === String(umiTrim));
          if (acq) {
            downloadReport(acq, match);
          }
        }
      },
    });

    clearReportDeepLinkSearchParams(setSearchParams);

    return undefined;
  }, [organizations, searchParams]);

  const renderActionPopover = () => {
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const protocolId = selectedAcquisition.acquisition_id;
    const protocolEncoded = btoa(protocolId);
    const organizationEncoded = btoa(organization.physio_org_id);
    /* eslint-disable-next-line max-len */
    const analyticsURL = `${ANALYTICS_REPORTS_URL}/home#!/analysis-tool/return/Y2xvc2Vfd2luZG93/organization/${organizationEncoded}/acquisitions/${protocolEncoded}`;
    const encodedAnalyticsURL = btoa(analyticsURL);
    const encodedLocation = btoa(window.location);
    /* eslint-disable-next-line max-len */
    const authAndAnalyticsRedirectURL = `${ANALYTICS_REPORTS_URL}/auth-gateway?goTo=${encodedAnalyticsURL}&ifFail=${encodedLocation}`;

    return (
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          <AcquisitionMenuItem
            icon={<VisibilitySharpIcon />}
            onClick={() => {
              downloadReport(selectedAcquisition);
              setAnchorEl(null);
            }}
            data-testid="view-report-item"
            text={t('physio.physio-personal.button-report-view')}
          />
          <AcquisitionMenuItem
            icon={<QueryStatsSharpIcon />}
            onClick={() => {
              const form = document.createElement('form');
              form.action = authAndAnalyticsRedirectURL;
              form.method = 'POST';
              form.target = '_blank';

              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = 'token';
              input.value = Cookies.get('access_token');
              form.appendChild(input);

              document.body.appendChild(form);
              form.submit();
              document.body.removeChild(form);

              setAnchorEl(null);
            }}
            data-testid="analytics-item"
            text="Analytics"
          />
        </List>
      </Popover>
    );
  };

  const renderMonitorizationTable = () =>
    acquisitions && acquisitions.length > 0 ? (
      <Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left">{t('physio.physio-personal.table-physio-protocol')}</TableCell>
                <TableCell align="left">{t('physio.physio-personal.table-physio-date')}</TableCell>
                <TableCell align="left">{t('physio.physio-personal.table-physio-duration')}</TableCell>
                <TableCell align="left">{t('physio.physio-personal.table-physio-doctor')}</TableCell>
                <TableCell align="left">&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {acquisitions.map((acquisition) => (
                <AnonymizedAcquisitionRow
                  key={acquisition.acquisition_id}
                  loadingDoctor={loadingDoctors}
                  acquisition={acquisition}
                  doctors={doctors}
                  onClickMenu={(event, acquisition) => {
                    setSelectedAcquisition(acquisition);
                    setAnchorEl(event.currentTarget);
                  }}
                  onClickProtocol={(acquisition) => downloadReport(acquisition)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {renderActionPopover()}
      </Box>
    ) : (
      <Typography
        variant="body2"
        color="secondary"
        data-testid={
          acquisitionsFetchSucceeded === true ? 'no-monitorization-text' : 'acquisitions-fetch-error-text'
        }
      >
        {acquisitionsFetchSucceeded === true
          ? t('physio.physio-personal.no-monitorizations-found')
          : t('messages.fetch-data-failure')}
      </Typography>
    );

  return (
    <App title={TITLE} breadcrumbs={breadcrumbs}>
      <Section className={styles.sectionFilters}>
        <Subtitle>{t('physio.physio-personal.title-filter-physio')}</Subtitle>
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <Grid marginTop="8px" container spacing="12px">
            <Grid item xs={12} sm={4} md={2}>
              <FormControl sx={{ margin: 0 }} fullWidth>
                <InputLabel>{t('physio.physio-personal.input-filter-protocol')}</InputLabel>
                <OutlinedInput
                  name="protocol"
                  value={protocol}
                  onChange={(event) => setProtocol(event.target.value)}
                  inputProps={{ 'data-testid': 'protocol-input' }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl sx={{ margin: 0 }} fullWidth>
                <Autocomplete
                  disablePortal
                  value={organization}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setOrganization(newValue);
                    }
                  }}
                  options={organizations}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <Typography
                      {...props}
                      variant="body2"
                      color="secondary"
                      key={option.id}
                      data-testid={`organization-option-${option.id}`}
                    >
                      {option.label}
                    </Typography>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label={t('physio.physio-personal.input-filter-organization')} />
                  )}
                  data-testid="organization-select"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl sx={{ margin: 0 }} fullWidth>
                <Autocomplete
                  disablePortal
                  value={doctor}
                  onChange={(event, newValue) => setDoctor(newValue)}
                  options={doctors}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <Typography
                      {...props}
                      variant="body2"
                      color="secondary"
                      key={option.id}
                      data-testid={`doctor-option-${option.id}`}
                    >
                      {option.label}
                    </Typography>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label={t('physio.physio-personal.input-filter-doctor')} />
                  )}
                  data-testid="doctor-select"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Button variant="outlined" onClick={() => setShowFilterByTags(true)} fullWidth data-testid="tag-button">
                {selectedTags.length === 0
                  ? t('physio.physio-personal.button-filter-by-tag')
                  : t('physio.physio-personal.button-filter-by-tag-selected', { number_tags: selectedTags.length })}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Button variant="outlined" onClick={clearFilters} fullWidth data-testid="reset-button">
                {t('physio.physio-personal.button-filter-reset')}
              </Button>{' '}
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Button type="submit" color="gradient" onClick={filterAcquisitions} fullWidth data-testid="apply-button">
                {t('physio.physio-personal.button-filter-apply')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Section>

      <Section>
        <Loader loading={loading}>
          <Fade>{renderMonitorizationTable()}</Fade>
        </Loader>
      </Section>

      <TagsSelectDialog
        tags={tags}
        selectedTags={selectedTags}
        show={showFilterByTags}
        onCheckboxClick={(tag) => {
          if (selectedTags.includes(tag)) {
            setSelectedTags(selectedTags.filter((selectedTag) => selectedTag !== tag));
            return;
          }

          setSelectedTags([...selectedTags, tag]);
        }}
        onDialogToggle={() => setShowFilterByTags(false)}
      />

      <Box display="flex" justifyContent="flex-end" alignItems="baseline" mt={2}>
        <Pagination count={count} onChange={onPaginationChange} page={currentPage} />
        <FormControl sx={{ width: 115, marginLeft: 3 }}>
          <InputLabel>{t('physio.physio-personal.input-items-per-page')}</InputLabel>
          <Select
            value={limitPerPage}
            onChange={onItensPerPageChange}
            label={t('physio.physio-personal.input-items-per-page')}
          >
            {LIMIT_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </App>
  );
}

export default AdminOrganizationReports;
